import { MonitorTableToolbar } from "@/ui/blocks/MonitorTableToolbar";
import Button from "@imminently/decisively-ui/Button";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import EmptyOverlay from "@imminently/decisively-ui/EmptyOverlay";
import Icon from "@imminently/decisively-ui/Icon";
import {
  Background,
  BackgroundVariant,
  ConnectionLineType,
  Controls,
  Panel,
  ReactFlow,
  ReactFlowProvider,
} from "@xyflow/react";
import { useOrgModelGraphExpansion } from "../hooks/useOrgModelGraphExpansion";
import { TYPE_OPTIONS, type OrgModelGraphProps } from "../types";
import { OrgModelGraphEntityNode } from "./OrgModelGraphEntityNode";
import { OrgModelGraphRecordNode } from "./OrgModelGraphRecordNode";

const nodeTypes = {
  orgModelRecord: OrgModelGraphRecordNode,
  orgModelEntity: OrgModelGraphEntityNode,
};

export const OrgModelGraph = ({
  selectedRecord,
  title = "Org Model Graph",
  emptyMessage = "Select a record to start exploring the graph",
  onClear,
}: OrgModelGraphProps) => {
  const { visibleGraphTypes, setGraphTypeVisible, layoutedGraph, hasGraphRecords, hasGraph, clearGraph } =
    useOrgModelGraphExpansion(selectedRecord, onClear);

  const hiddenTypesCount = TYPE_OPTIONS.length - visibleGraphTypes.length;
  const nodeVisibilitySummary = hasGraphRecords
    ? `${layoutedGraph.visibleRecordCount} visible, ${layoutedGraph.hiddenRecordCount} hidden`
    : null;
  const filterTooltip = `${visibleGraphTypes.length} of ${TYPE_OPTIONS.length} types shown`;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <MonitorTableToolbar
        title={title}
        actions={
          <>
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button
                  icon
                  variant="outline"
                  intent="secondary"
                  label="Show Types"
                  tooltip={filterTooltip}
                  className="relative"
                >
                  <Icon name="filter-list" />
                  {hiddenTypesCount > 0 ? (
                    <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground">
                      {hiddenTypesCount}
                    </span>
                  ) : null}
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content
                align="end"
                className="w-56"
              >
                <DropdownMenu.Label className="text-muted-foreground">Show Types</DropdownMenu.Label>
                {TYPE_OPTIONS.map((type) => (
                  <DropdownMenu.CheckboxItem
                    key={type}
                    checked={visibleGraphTypes.includes(type)}
                    onSelect={(event) => event.preventDefault()}
                    onCheckedChange={(checked) => setGraphTypeVisible(type, checked)}
                  >
                    <DropdownMenu.Item.Label>{type}</DropdownMenu.Item.Label>
                  </DropdownMenu.CheckboxItem>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu>
            <Button
              icon
              variant="outline"
              intent="secondary"
              label="Clear graph"
              onClick={clearGraph}
            >
              <Icon name="reset" />
            </Button>
          </>
        }
      />

      <div className="min-h-0 flex-1">
        {!hasGraph ? (
          <EmptyOverlay message={hasGraphRecords ? "No graph records match the selected type filters" : emptyMessage} />
        ) : (
          <ReactFlowProvider>
            <ReactFlow
              nodes={layoutedGraph.nodes}
              edges={layoutedGraph.edges}
              nodeTypes={nodeTypes}
              fitView
              fitViewOptions={{ padding: 0.15 }}
              minZoom={0.1}
              nodesConnectable={false}
              connectionLineType={ConnectionLineType.SmoothStep}
            >
              <Background
                variant={BackgroundVariant.Lines}
                gap={28}
                size={1}
                color="#efefef"
              />
              <Controls />
              {nodeVisibilitySummary ? (
                <Panel
                  position="top-right"
                  className="rounded-md border border-border bg-card px-2 py-1 text-xs text-muted-foreground shadow-sm"
                >
                  {nodeVisibilitySummary}
                </Panel>
              ) : null}
            </ReactFlow>
          </ReactFlowProvider>
        )}
      </div>
    </div>
  );
};

export default OrgModelGraph;
